import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Environment } from "@/api/environments";
import { setActiveEnvSlug } from "@/api/active-env";

interface EnvState {
  activeSlug: string | null;
  environments: Environment[];
  limit: number;
  unlimited: boolean;
  /** The currently-selected environment object (if loaded). */
  active: () => Environment | undefined;
  /** The caller's effective role in the active environment (per-env RBAC), or
   * undefined on OSS/self-host → callers treat undefined as allow-all. */
  activeRole: () => Environment["role"];
  setActive: (slug: string) => void;
  /** Replace the loaded list; keeps the active selection if still valid,
   * otherwise falls back to Production (or the first environment). */
  setEnvironments: (envs: Environment[], limit: number, unlimited: boolean) => void;
}

export const useEnvStore = create<EnvState>()(
  persist(
    (set, get) => ({
      activeSlug: null,
      environments: [],
      limit: 0,
      unlimited: false,
      active: () => get().environments.find((e) => e.slug === get().activeSlug),
      activeRole: () => get().active()?.role,
      setActive: (slug) => {
        setActiveEnvSlug(slug);
        set({ activeSlug: slug });
      },
      setEnvironments: (environments, limit, unlimited) => {
        const cur = get().activeSlug;
        const valid = environments.some((e) => e.slug === cur);
        const fallback =
          environments.find((e) => e.type === "production")?.slug ?? environments[0]?.slug ?? null;
        const activeSlug = valid ? cur : fallback;
        setActiveEnvSlug(activeSlug);
        set({ environments, limit, unlimited, activeSlug });
      },
    }),
    {
      name: "deleggate-env",
      partialize: (s) => ({ activeSlug: s.activeSlug }),
      // Push the rehydrated slug into the API client before any request fires.
      onRehydrateStorage: () => (state) => {
        if (state?.activeSlug) setActiveEnvSlug(state.activeSlug);
      },
    },
  ),
);
